import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import MenuItemCard from '../components/MenuItemCard';
import { Search, MapPin } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useTenant } from '../context/TenantContext';
import Loading from '../components/Loading';

export default function Menu() {
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedBranchId, setSelectedBranchId] = useState('');
    const [loading, setLoading] = useState(true);
    const { itemCount, cartTotal } = useCart();
    const { tenant, isFranchise, branches, franchise } = useTenant();

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const waRef = urlParams.get('wa_ref');
        if (waRef) {
            sessionStorage.setItem('wa_ref', waRef);
        }
    }, []);

    useEffect(() => {
        if (tenant?.id) {
            setSelectedBranchId(tenant.id);
        } else if (isFranchise && branches && branches.length > 0) {
            const savedBranchSlug = localStorage.getItem('active_branch_slug');
            const match = branches.find(b => b.slug === savedBranchSlug);
            if (match) {
                setSelectedBranchId(match.id);
            } else {
                setSelectedBranchId(branches[0].id);
                localStorage.setItem('active_branch_slug', branches[0].slug);
            }
        }
    }, [tenant?.id, isFranchise, branches]);

    const activeRestaurantId = tenant?.id || selectedBranchId;

    useEffect(() => {
        if (activeRestaurantId) {
            fetchCategories();
            fetchItems();
        } else {
            setLoading(false);
        }
    }, [activeRestaurantId, selectedCategory, searchQuery]);

    const fetchCategories = async () => {
        if (!activeRestaurantId) return;
        try {
            const response = await api.get(`/api/menu/categories?restaurantId=${activeRestaurantId}`);
            setCategories(response.data);
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const fetchItems = async () => {
        if (!activeRestaurantId) return;
        try {
            setLoading(true);
            const params = { restaurantId: activeRestaurantId };
            if (selectedCategory) params.categoryId = selectedCategory;
            if (searchQuery) params.search = searchQuery;
            
            const response = await api.get('/api/menu/items', { params });
            setItems(response.data);
        } catch (error) {
            console.error('Error fetching items:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:pb-8 pb-24`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-bold">Our Menu</h1>
                    {isFranchise && franchise && (
                        <p className="text-slate-500 text-sm mt-1">Select your branch location to view available delicacies.</p>
                    )}
                </div>

                {isFranchise && branches && branches.length > 0 && (
                    <div className="flex items-center gap-2 bg-slate-100 p-2 rounded-2xl border border-slate-200">
                        <MapPin className="w-4 h-4 text-primary-600 ml-1" />
                        <span className="text-xs font-bold text-slate-600 whitespace-nowrap">Branch:</span>
                        <select
                            value={selectedBranchId}
                            onChange={(e) => {
                                const newId = e.target.value;
                                setSelectedBranchId(newId);
                                const b = branches.find(item => String(item.id) === String(newId));
                                if (b) localStorage.setItem('active_branch_slug', b.slug);
                            }}
                            className="bg-white border border-slate-200 text-slate-800 text-xs font-bold rounded-xl px-3 py-1.5 focus:ring-2 focus:ring-primary-500 outline-none shadow-sm"
                        >
                            {branches.map(b => (
                                <option key={b.id} value={b.id}>{b.name}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>
            
            {/* Search and Filter */}
            <div className="flex flex-col md:flex-row gap-4 mb-8">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                        type="text"
                        placeholder="Search menu..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                </div>
                <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                    <option value="">All Categories</option>
                    {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                </select>
            </div>

            {/* Menu Grid */}
            {loading ? (
                <Loading mode="page" message="Browsing available delicacies..." />
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {items.map(item => (
                        <MenuItemCard key={item.id} item={item} />
                    ))}
                </div>
            )}
            
            {!loading && items.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                    No items found matching your criteria
                </div>
            )}
        </div>
    );
}